import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, X, ArrowRight, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { TOUR_STEPS } from "./portalPreviewData";
import DemoNavigatorSpotlight from "./DemoNavigatorSpotlight";

/**
 * Scripted stand-in for the real Client Portal's Navigator agent, scoped to
 * the public /portal-preview demo. Deliberately NOT a live LLM chat: this
 * route is unauthenticated and reachable by anyone, so a live agent here
 * would be an unmetered public AI-cost/abuse surface. Instead it plays a
 * fixed script and drives the same pointer/spotlight visual the real
 * Navigator uses, so prospects see the actual mechanic without the open
 * attack surface.
 */
export default function DemoNavigatorTour({ activeTab, onNavigate }) {
  const [open, setOpen] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const started = open;
  const step = TOUR_STEPS[stepIndex];

  const goTo = (index) => {
    const next = TOUR_STEPS[index];
    if (!next) return;
    setStepIndex(index);
    if (next.tab && next.tab !== activeTab) onNavigate?.(next.tab);
  };

  const start = () => {
    setOpen(true);
    setStepIndex(0);
    if (TOUR_STEPS[0].tab && TOUR_STEPS[0].tab !== activeTab) onNavigate?.(TOUR_STEPS[0].tab);
  };

  const close = () => setOpen(false);

  return (
    <>
      {started && <DemoNavigatorSpotlight targetId={step.targetId} />}

      {!started && (
        <motion.button
          type="button"
          onClick={start}
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          className="fixed bottom-6 right-4 sm:right-6 z-40 flex items-center gap-2 bg-secondary text-secondary-foreground text-sm font-semibold px-4 py-3 rounded-full shadow-lg hover:bg-secondary/90 transition-colors"
        >
          <Sparkles className="w-4 h-4" />
          Take the guided tour
        </motion.button>
      )}

      <AnimatePresence>
        {started && (
          <motion.div
            key="demo-tour-card"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 16 }}
            className="fixed bottom-6 right-4 sm:right-6 z-40 w-[92vw] max-w-sm bg-card border border-border rounded-2xl shadow-2xl p-4"
          >
            <div className="flex items-start justify-between gap-2 mb-2">
              <div className="flex items-center gap-1.5 text-xs font-semibold text-secondary">
                <Sparkles className="w-3.5 h-3.5" />
                Demo Navigator
              </div>
              <button type="button" onClick={close} aria-label="Close tour" className="text-muted-foreground hover:text-foreground">
                <X className="w-4 h-4" />
              </button>
            </div>
            <h4 className="font-semibold text-sm text-foreground mb-1">{step.title}</h4>
            <p className="text-sm text-muted-foreground leading-relaxed mb-4">{step.text}</p>
            <div className="flex items-center justify-between">
              <span className="text-[11px] text-muted-foreground">
                Step {stepIndex + 1} of {TOUR_STEPS.length}
              </span>
              <div className="flex gap-2">
                {stepIndex > 0 && (
                  <Button size="sm" variant="outline" onClick={() => goTo(stepIndex - 1)}>
                    <ArrowLeft className="w-3.5 h-3.5" />
                  </Button>
                )}
                {stepIndex < TOUR_STEPS.length - 1 ? (
                  <Button size="sm" onClick={() => goTo(stepIndex + 1)}>
                    Next <ArrowRight className="w-3.5 h-3.5 ml-1" />
                  </Button>
                ) : (
                  <Button size="sm" onClick={close}>
                    Done
                  </Button>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
